import { useCallback, useMemo, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from 'reactflow';
import 'reactflow/dist/style.css';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Save, Play, Pause, RotateCcw, Send, ArrowLeft, X,
  MessageSquare, Edit3, GitBranch, Globe, Clock, CreditCard, BookOpen, Square,
} from 'lucide-react';
import { api } from '../api/client';

export type BotNodeType =
  | 'message'
  | 'input'
  | 'condition'
  | 'api_call'
  | 'delay'
  | 'payment_request'
  | 'faq_query'
  | 'end';

export interface BotNodeData {
  label: string;
  nodeType: BotNodeType;
  // message
  text?: string;
  buttons?: Array<{ text: string; callback_data: string }>;
  // input
  variable?: string;
  validation?: 'text' | 'number' | 'email';
  // condition
  operator?: '=' | '!=' | '>' | '<';
  value?: string;
  // api_call
  url?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: string;
  body?: string;
  // payment_request
  amount?: number;
  currency?: string;
  planId?: string;
  // delay
  delayMs?: number;
  // faq_query
  knowledgeBase?: string;
}

export interface BotFlow {
  nodes: Node<BotNodeData>[];
  edges: Edge[];
}

export interface BotFlowEditorProps {
  botId: string;
  initialFlow?: BotFlow;
  onBack: () => void;
}

const NODE_TYPES: BotNodeType[] = [
  'message', 'input', 'condition', 'api_call', 'delay', 'payment_request', 'faq_query', 'end',
];

const NODE_ICONS: Record<BotNodeType, React.ComponentType<{ size?: number | string }>> = {
  message: MessageSquare,
  input: Edit3,
  condition: GitBranch,
  api_call: Globe,
  delay: Clock,
  payment_request: CreditCard,
  faq_query: BookOpen,
  end: Square,
};

const NODE_COLORS: Record<BotNodeType, string> = {
  message: 'bg-blue-100 border-blue-300',
  input: 'bg-purple-100 border-purple-300',
  condition: 'bg-yellow-100 border-yellow-300',
  api_call: 'bg-green-100 border-green-300',
  delay: 'bg-slate-100 border-slate-300',
  payment_request: 'bg-pink-100 border-pink-300',
  faq_query: 'bg-indigo-100 border-indigo-300',
  end: 'bg-red-100 border-red-300',
};

function defaultDataFor(nodeType: BotNodeType, label: string): BotNodeData {
  const base: BotNodeData = { label, nodeType };
  switch (nodeType) {
    case 'message': return { ...base, text: '', buttons: [] };
    case 'input': return { ...base, variable: '', validation: 'text' };
    case 'condition': return { ...base, variable: '', operator: '=', value: '' };
    case 'api_call': return { ...base, url: '', method: 'GET', headers: '{}', body: '' };
    case 'payment_request': return { ...base, amount: 0, currency: 'USD', planId: '' };
    case 'delay': return { ...base, delayMs: 1000 };
    case 'faq_query': return { ...base, knowledgeBase: '' };
    case 'end': return base;
  }
}

export function BotFlowEditor({ botId, initialFlow, onBack }: BotFlowEditorProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [nodes, setNodes] = useState<Node<BotNodeData>[]>(initialFlow?.nodes ?? []);
  const [edges, setEdges] = useState<Edge[]>(initialFlow?.edges ?? []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [testOpen, setTestOpen] = useState(false);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );
  const onConnect = useCallback(
    (conn: Connection) => setEdges((eds) => addEdge(conn, eds)),
    [],
  );

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedId) ?? null,
    [nodes, selectedId],
  );

  const updateNodeData = useCallback((id: string, patch: Partial<BotNodeData>): void => {
    setNodes((nds) => nds.map((n) =>
      n.id === id ? { ...n, data: { ...n.data, ...patch } } : n,
    ));
  }, []);

  const onDragStart = (e: DragEvent<HTMLDivElement>, nodeType: BotNodeType): void => {
    e.dataTransfer.setData('application/reactflow-bot-node', nodeType);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const nodeType = e.dataTransfer.getData('application/reactflow-bot-node') as BotNodeType;
    if (!nodeType) return;
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const position = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const id = `n_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const label = t(`bots.nodes.${nodeType}`);
    const newNode: Node<BotNodeData> = {
      id,
      type: 'default',
      position,
      data: defaultDataFor(nodeType, label),
      className: clsx('rounded border px-3 py-2 text-sm font-medium', NODE_COLORS[nodeType]),
    };
    setNodes((nds) => [...nds, newNode]);
  }, [t]);

  // Programmatic add (for tests / accessibility — no real DnD in jsdom)
  const addNodeOfType = useCallback((nodeType: BotNodeType): void => {
    const id = `n_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const label = t(`bots.nodes.${nodeType}`);
    const newNode: Node<BotNodeData> = {
      id,
      type: 'default',
      position: { x: 100 + nodes.length * 40, y: 100 + nodes.length * 40 },
      data: defaultDataFor(nodeType, label),
      className: clsx('rounded border px-3 py-2 text-sm font-medium', NODE_COLORS[nodeType]),
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedId(id);
  }, [nodes.length, t]);

  const saveMut = useMutation({
    mutationFn: async () =>
      api.put(`/bots/${botId}/flow`, { nodes, edges }),
    onSuccess: () => {
      toast.success(t('bots.saved'));
      void qc.invalidateQueries({ queryKey: ['bots'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const deployMut = useMutation({
    mutationFn: async () => api.post(`/bots/${botId}/deploy`),
    onSuccess: () => {
      toast.success(t('bots.deployed'));
      void qc.invalidateQueries({ queryKey: ['bots'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const pauseMut = useMutation({
    mutationFn: async () => api.post(`/bots/${botId}/pause`),
    onSuccess: () => {
      toast.success(t('bots.paused'));
      void qc.invalidateQueries({ queryKey: ['bots'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const resumeMut = useMutation({
    mutationFn: async () => api.post(`/bots/${botId}/resume`),
    onSuccess: () => {
      toast.success(t('bots.resumed'));
      void qc.invalidateQueries({ queryKey: ['bots'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white">
        <button
          className="btn-secondary flex items-center gap-2"
          onClick={onBack}
          aria-label={t('bots.back')}
        >
          <ArrowLeft size={16} /> {t('bots.back')}
        </button>
        <div className="flex gap-2">
          <button
            className="btn-primary flex items-center gap-2"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
          >
            <Save size={16} /> {t('bots.save')}
          </button>
          <button
            className="btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
            onClick={() => deployMut.mutate()}
            disabled={deployMut.isPending}
          >
            <Play size={16} /> {t('bots.deploy')}
          </button>
          <button
            className="btn-secondary flex items-center gap-2"
            onClick={() => setTestOpen(true)}
          >
            <Send size={16} /> {t('bots.testRun')}
          </button>
          <button
            className="btn-secondary flex items-center gap-2"
            onClick={() => pauseMut.mutate()}
            disabled={pauseMut.isPending}
          >
            <Pause size={16} /> {t('bots.pause')}
          </button>
          <button
            className="btn-secondary flex items-center gap-2"
            onClick={() => resumeMut.mutate()}
            disabled={resumeMut.isPending}
          >
            <RotateCcw size={16} /> {t('bots.resume')}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — node palette */}
        <aside
          className="w-56 border-r border-slate-200 bg-slate-50 p-3 overflow-y-auto"
          aria-label={t('bots.nodes.title')}
        >
          <h3 className="text-xs font-semibold text-brand-muted uppercase mb-2">
            {t('bots.nodes.title')}
          </h3>
          <div className="space-y-2">
            {NODE_TYPES.map((nodeType) => {
              const Icon = NODE_ICONS[nodeType];
              return (
                <div
                  key={nodeType}
                  role="button"
                  tabIndex={0}
                  draggable
                  onDragStart={(e) => onDragStart(e, nodeType)}
                  onClick={() => addNodeOfType(nodeType)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addNodeOfType(nodeType); }}
                  data-testid={`palette-${nodeType}`}
                  className={clsx(
                    'flex items-center gap-2 px-3 py-2 rounded border cursor-grab text-sm font-medium hover:shadow-sm',
                    NODE_COLORS[nodeType],
                  )}
                >
                  <Icon size={14} />
                  {t(`bots.nodes.${nodeType}`)}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Canvas */}
        <div
          className="flex-1 bg-slate-50 relative"
          onDrop={onDrop}
          onDragOver={onDragOver}
          data-testid="bot-flow-canvas"
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => setSelectedId(node.id)}
            onPaneClick={() => setSelectedId(null)}
            fitView
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>
        </div>

        {/* Properties panel */}
        <aside className="w-72 border-l border-slate-200 bg-white p-3 overflow-y-auto">
          <h3 className="text-xs font-semibold text-brand-muted uppercase mb-2">
            {t('bots.props.title')}
          </h3>
          {!selectedNode ? (
            <p className="text-sm text-brand-muted">{t('bots.props.none')}</p>
          ) : (
            <NodePropsPanel node={selectedNode} update={(p) => updateNodeData(selectedNode.id, p)} />
          )}
        </aside>
      </div>

      {testOpen && (
        <TestRunModal
          botId={botId}
          flow={{ nodes, edges }}
          onClose={() => setTestOpen(false)}
        />
      )}
    </div>
  );
}

/* =========================== Properties Panels =========================== */

function NodePropsPanel({
  node,
  update,
}: {
  node: Node<BotNodeData>;
  update: (patch: Partial<BotNodeData>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const d = node.data;

  switch (d.nodeType) {
    case 'message':
      return (
        <div className="space-y-3">
          <FieldSm label={t('bots.props.text')}>
            <textarea
              className="input w-full"
              rows={4}
              value={d.text ?? ''}
              onChange={(e) => update({ text: e.target.value })}
            />
          </FieldSm>
          <FieldSm label={t('bots.props.buttons')}>
            <div className="space-y-2">
              {(d.buttons ?? []).map((b, i) => (
                <div key={i} className="flex gap-1">
                  <input
                    className="input flex-1 text-xs"
                    placeholder={t('bots.props.buttonText')}
                    value={b.text}
                    onChange={(e) => {
                      const next = [...(d.buttons ?? [])];
                      next[i] = { ...next[i], text: e.target.value };
                      update({ buttons: next });
                    }}
                  />
                  <input
                    className="input flex-1 text-xs font-mono"
                    placeholder={t('bots.props.callbackData')}
                    value={b.callback_data}
                    onChange={(e) => {
                      const next = [...(d.buttons ?? [])];
                      next[i] = { ...next[i], callback_data: e.target.value };
                      update({ buttons: next });
                    }}
                  />
                  <button
                    type="button"
                    className="text-red-600 px-1"
                    aria-label="remove-button"
                    onClick={() => {
                      const next = (d.buttons ?? []).filter((_, j) => j !== i);
                      update({ buttons: next });
                    }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn-secondary text-xs"
                onClick={() => update({
                  buttons: [...(d.buttons ?? []), { text: '', callback_data: '' }],
                })}
              >
                + {t('bots.props.addButton')}
              </button>
            </div>
          </FieldSm>
        </div>
      );
    case 'input':
      return (
        <div className="space-y-3">
          <FieldSm label={t('bots.props.variable')}>
            <input
              className="input w-full"
              value={d.variable ?? ''}
              onChange={(e) => update({ variable: e.target.value })}
            />
          </FieldSm>
          <FieldSm label={t('bots.props.validation')}>
            <select
              className="input w-full"
              value={d.validation ?? 'text'}
              onChange={(e) => update({ validation: e.target.value as BotNodeData['validation'] })}
            >
              <option value="text">text</option>
              <option value="number">number</option>
              <option value="email">email</option>
            </select>
          </FieldSm>
        </div>
      );
    case 'condition':
      return (
        <div className="space-y-3">
          <FieldSm label={t('bots.props.variable')}>
            <input
              className="input w-full"
              value={d.variable ?? ''}
              onChange={(e) => update({ variable: e.target.value })}
            />
          </FieldSm>
          <FieldSm label={t('bots.props.operator')}>
            <select
              className="input w-full"
              value={d.operator ?? '='}
              onChange={(e) => update({ operator: e.target.value as BotNodeData['operator'] })}
            >
              <option value="=">=</option>
              <option value="!=">!=</option>
              <option value=">">{'>'}</option>
              <option value="<">{'<'}</option>
            </select>
          </FieldSm>
          <FieldSm label={t('bots.props.value')}>
            <input
              className="input w-full"
              value={d.value ?? ''}
              onChange={(e) => update({ value: e.target.value })}
            />
          </FieldSm>
        </div>
      );
    case 'api_call':
      return (
        <div className="space-y-3">
          <FieldSm label={t('bots.props.url')}>
            <input
              className="input w-full font-mono text-xs"
              value={d.url ?? ''}
              onChange={(e) => update({ url: e.target.value })}
            />
          </FieldSm>
          <FieldSm label={t('bots.props.method')}>
            <select
              className="input w-full"
              value={d.method ?? 'GET'}
              onChange={(e) => update({ method: e.target.value as BotNodeData['method'] })}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </select>
          </FieldSm>
          <FieldSm label={t('bots.props.headers')}>
            <textarea
              className="input w-full font-mono text-xs"
              rows={3}
              value={d.headers ?? ''}
              onChange={(e) => update({ headers: e.target.value })}
            />
          </FieldSm>
          <FieldSm label={t('bots.props.body')}>
            <textarea
              className="input w-full font-mono text-xs"
              rows={3}
              value={d.body ?? ''}
              onChange={(e) => update({ body: e.target.value })}
            />
          </FieldSm>
        </div>
      );
    case 'payment_request':
      return (
        <div className="space-y-3">
          <FieldSm label={t('bots.props.amount')}>
            <input
              type="number"
              className="input w-full"
              value={d.amount ?? 0}
              onChange={(e) => update({ amount: Number(e.target.value) })}
            />
          </FieldSm>
          <FieldSm label={t('bots.props.currency')}>
            <select
              className="input w-full"
              value={d.currency ?? 'USD'}
              onChange={(e) => update({ currency: e.target.value })}
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="RUB">RUB</option>
            </select>
          </FieldSm>
          <FieldSm label={t('bots.props.planId')}>
            <input
              className="input w-full"
              value={d.planId ?? ''}
              onChange={(e) => update({ planId: e.target.value })}
            />
          </FieldSm>
        </div>
      );
    case 'delay':
      return (
        <FieldSm label={t('bots.props.delayMs')}>
          <input
            type="number"
            className="input w-full"
            value={d.delayMs ?? 0}
            onChange={(e) => update({ delayMs: Number(e.target.value) })}
          />
        </FieldSm>
      );
    case 'faq_query':
      return (
        <FieldSm label={t('bots.props.knowledgeBase')}>
          <input
            type="file"
            accept=".pdf,.txt"
            className="block text-sm"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) update({ knowledgeBase: file.name });
            }}
          />
          {d.knowledgeBase && (
            <p className="text-xs text-brand-muted mt-1">{d.knowledgeBase}</p>
          )}
        </FieldSm>
      );
    case 'end':
      return <p className="text-sm text-brand-muted">—</p>;
  }
}

function FieldSm({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-medium text-brand-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

/* =========================== Test Run Modal =========================== */

interface TestTrace {
  nodeId: string;
  label: string;
  output?: string;
}

function TestRunModal({
  botId,
  flow,
  onClose,
}: {
  botId: string;
  flow: BotFlow;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [trace, setTrace] = useState<TestTrace[]>([]);

  const mut = useMutation({
    mutationFn: async () =>
      (await api.post<{ trace: TestTrace[] }>(`/bots/${botId}/test-run`, { message, flow })).data,
    onSuccess: (data) => {
      setTrace(data.trace ?? []);
    },
    onError: () => toast.error(t('common.error')),
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('bots.testRun')}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{t('bots.testRun')}</h3>
          <button onClick={onClose} aria-label="close" className="text-brand-muted hover:text-brand-ink">
            <X size={18} />
          </button>
        </div>

        <form
          className="space-y-3"
          onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
        >
          <label className="block text-sm font-medium">{t('bots.testInput')}</label>
          <input
            className="input w-full"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <button
            type="submit"
            className="btn-primary"
            disabled={mut.isPending}
          >
            {t('bots.testStart')}
          </button>
        </form>

        {trace.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold mb-2">{t('bots.testTrace')}</h4>
            <ol className="space-y-1">
              {trace.map((step, i) => (
                <li
                  key={`${step.nodeId}-${i}`}
                  className="border-l-4 border-brand-primary bg-brand-primary/5 pl-3 py-1 text-sm"
                  data-testid={`trace-step-${i}`}
                >
                  <span className="font-medium">{step.label}</span>
                  {step.output && <span className="text-brand-muted ml-2">→ {step.output}</span>}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
